import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAudioPlayer } from "../../contexts/AudioPlayerContext";
import {
  PlayFill,
  PauseFill,
  ThumbsUpFill,
  ArrowUpRightFromSquare,
  ArrowShapeDownToLine,
  NodesRight,
} from "@gravity-ui/icons";
import apiService from "../../api/apiService";
import Toast from "../generic_components/Toast";
import ProgressInfoToast from "../custom_components/ProgressInfoToast";
import AudioProcessor from "./AudioProcessor";
import { useLocalState } from "../../contexts/LocalStateProvider";
import { usePostHog } from "posthog-js/react";
import { getCookie } from "../../utils/cookieUtils";
import DownloadModal from "./DownloadModal";
import * as Sentry from "@sentry/react"
import { getPartitionedLocalStorage } from "../../utils/tokenExtraction";
const ProjectLikedTracksList = ({ tracks }) => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const {
    isPlaying,
    playAudio,
    pauseAudio,
    resumeAudio,
    audioSrc: currentAudioSrc,
    setDiscoverId,
  } = useAudioPlayer();
  const { updateLocalState } = useLocalState();

  const [toastMessage, setToastMessage] = useState(null);
  const [toastType, setToastType] = useState("error");
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [selectedTrack, setSelectedTrack] = useState(null);
  const [isGeneratingStems, setIsGeneratingStems] = useState(false);
  const [stemProgress, setStemProgress] = useState(0);
  const [activeTooltip, setActiveTooltip] = useState({ trackId: null, type: null });
  const [likedTracks, setLikedTracks] = useState(tracks || []);

  useEffect(() => {
    setLikedTracks(tracks || []);
  }, [tracks]);

  const handleDownload = (track) => {
    setSelectedTrack(track);
    setIsDownloadModalOpen(true);
    posthog.capture("Project Liked Tracks - Click on Download", {is_user_logged_in: true});
  };

  const handlePlayPause = (track) => {
    if (isPlaying && currentAudioSrc === track.audioSrc) {
      pauseAudio();
    } else if (currentAudioSrc === track.audioSrc) {
      resumeAudio();
    } else {
      setDiscoverId(null);
      playAudio(
        track.audioSrc,
        track.name,
        track.description,
        track.imageSrc,
        track.duration
      );
    }
  };

  const formatTime = (durationInSeconds) => {
    const minutes = Math.floor(durationInSeconds / 60);
    const seconds = Math.floor(durationInSeconds % 60);
    return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
  };

  const navigateToProject = (projectId, responseId) => {
    posthog.capture("Project Liked Tracks - Click on Go to Project", {is_user_logged_in: true});
    updateLocalState("scrollToAiResponse", responseId);
    navigate(`/chatPage?projectId=${projectId}`);
  };

  const handleUnlike = async (e, track) => {
    e.stopPropagation();
    const userId = getCookie("user_id") || getPartitionedLocalStorage("user_id");

    if (!userId || !track.response_id) return;

    const payload = {
      response_id: track.response_id,
      user_id: userId,
      is_like: true, // We send true because we're toggling an existing like
    };

    try {
      posthog.capture("Project Liked Tracks - Unlike Track", {is_user_logged_in: true});

      // Remove the like from the backend
      await apiService.sendRequest("removeAIResponseLike", payload);

      // Update local state to remove the track from liked tracks
      const currentLikedTracks = await apiService.handleProtectedRoute(
        "fetchLikedProjectTracks",
        {}
      );

      const processedData = currentLikedTracks.map((track) => ({
        ...track,
        audioSrc:
          track.is_streaming && track.request_id
            ? `${process.env.REACT_APP_SERVER_ENDPOINT}/api/audio-stream/${track.request_id}/0`
            : `${process.env.REACT_APP_SERVER_ENDPOINT}/api/stream-audio/${track.response_id}/0`,
        imageSrc: track.signedUrlExists
          ? track.cover_image_name
          : !track.signedUrlExists && track.cover_image_name
          ? `/project-images/${track.cover_image_name}`
          : "/project-images/Album_Art_6.webp",
      }));

      updateLocalState("likedProjectTracks", processedData);

      // Show success toast
      setToastMessage("Track removed from liked tracks");
      setToastType("success");
      setTimeout(() => setToastMessage(null), 3000);
    } catch (error) {
      console.error("Error unliking track:", error);
      Sentry.captureException(error);
      setToastMessage("Failed to unlike track");
      setToastType("error");
      setTimeout(() => setToastMessage(null), 3000);
    }
  };

  const handleGetStems = async (e, track) => {
    e.stopPropagation();
    posthog.capture("Click-GetStems-LikedTracksPage", {
      is_user_logged_in: true,
      trackId: track.response_id,
      trackName: track.name
    });

    setSelectedTrack(track);
    setIsGeneratingStems(true);
    setStemProgress(0);
    setToastMessage(null);

    console.log(`[ProjectLikedTracksList] Initiating stem generation for response ID: ${track.response_id}`);

    try {
      // 1. Fetch GCS URI
      setStemProgress(1);
      const gcsResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/ai-response-gcs-uri/${track.response_id}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          }
        }
      );
      const gcsData = await gcsResponse.json();

      if (!gcsData.success || !gcsData.gcsUri) {
        console.error("[ProjectLikedTracksList] GCS URI Extraction Failed:", gcsData);
        setToastMessage(gcsData.message || "Could not find the audio file URI needed for stems.");
        setToastType("error");
        throw new Error("GCS URI Extraction Failed");
      }
      const gcsUri = gcsData.gcsUri;
      console.log(`[ProjectLikedTracksList] GCS URI: ${gcsUri}`);

      // 2. Call process-audio endpoint
      const requestId = Date.now().toString();
      setStemProgress(2);

      const processResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/process-audio`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          },
          body: JSON.stringify({ gcs_uri: gcsUri, request_id: requestId })
        }
      );
      const processData = await processResponse.json();

      if (!processResponse.ok || !processData.success) {
        console.error("[ProjectLikedTracksList] Stem separation failed:", processData);
        setToastMessage(processData.message || processData.details || "Stem separation failed.");
        setToastType("error");
        throw new Error("Stem Separation Failed");
      }

      console.log("[ProjectLikedTracksList] Stem separation successful:", processData);
      setStemProgress(3);
      setToastMessage("Stems generated! Downloading...");
      setToastType("info");

      // 3. Download stems
      const stemPaths = Object.values(processData.uploaded_files);
      const downloadResponse = await fetch(
        `${process.env.REACT_APP_SERVER_ENDPOINT}/api/download-stems`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${getCookie("token") || getPartitionedLocalStorage("token")}`
          },
          body: JSON.stringify({ stems: stemPaths })
        }
      );

      if (downloadResponse.ok) {
        const finalName = track?.name || 'Audio';
        const blob = await downloadResponse.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${finalName}_Stems_by_Wubble.zip`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        setToastMessage("Your stems have been downloaded successfully!");
        setToastType("success");
        setTimeout(() => setToastMessage(null), 5000);

      } else {
        console.error("[ProjectLikedTracksList] Error downloading stems:", downloadResponse);
        setToastMessage("Stems generated but download failed.");
        setToastType("error");
      }

    } catch (error) {
      console.error("[ProjectLikedTracksList] Error during stem process:", error);
      Sentry.captureException(error);
      if (!toastMessage) {
          setToastMessage("An error occurred during stem generation.");
          setToastType("error");
      }
    } finally {
      setIsGeneratingStems(false);
      setSelectedTrack(null);
      if (toastType === 'error') {
        setTimeout(() => setToastMessage(null), 8000);
      }
    }
  };

  return (
    <div className="w-full">
      {likedTracks.map((track) => (
        <div
          key={track.response_id}
          onClick={() => handlePlayPause(track)}
          style={
            isPlaying && currentAudioSrc === track.audioSrc
              ? {
                  background:
                    "linear-gradient(0deg, rgba(0, 0, 0, 0.72) 0%, rgba(0, 0, 0, 0.72) 100%), linear-gradient(135deg, #FC10F2 0%, #3078E4 100%)",
                }
              : {}
          }
          className={`flex items-center p-1.5 pr-4 m-1.5 rounded-xl cursor-pointer group relative ${
            isPlaying && currentAudioSrc === track.audioSrc
              ? "bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white"
              : "hover:bg-opacity-10 transition duration-500 hover:bg-white"
          }`}
        >
          <div className="flex items-center relative overflow-hidden mr-3">
            <img
              src={track.imageSrc}
              alt={track.name}
              className="w-12 h-12 object-cover rounded-lg"
            />
            <button
              id="project-play-pause-button"
              onClick={(e) => {
                e.stopPropagation();
                handlePlayPause(track);
              }}
              className={`absolute inset-0 flex items-center justify-center text-white transition-transform duration-500 transform group-hover:opacity-100 ${
                isPlaying && currentAudioSrc === track.audioSrc
                  ? "md:translate-y-0"
                  : "md:translate-y-[70%] md:group-hover:translate-y-0"
              }`}
            >
              {isPlaying && currentAudioSrc === track.audioSrc ? (
                <PauseFill className="w-4 h-4" />
              ) : (
                <PlayFill className="w-4 h-4" />
              )}
            </button>
          </div>

          <div className="flex-1 min-w-0 mr-2">
            <p className="font-medium text-base truncate">{track.name}</p>
            <p className="font-light text-sm text-gray-400 md:w-full truncate w-7/8">
              {track.description}
            </p>
          </div>

          <div className="flex flex-1 justify-end">
            <div className="hidden md:flex flex-1 min-w-0">
              <p className="font-light text-sm text-gray-400 truncate w-2/3">
                {track.projectname}
              </p>
            </div>

            <div className="hidden md:flex w-20">
              <span className="font-light text-sm text-white">
                {typeof track.duration === "string"
                  ? track.duration
                  : formatTime(track.duration) || "0:00"}
              </span>
            </div>

            <div className="flex justify-end items-center md:gap-6 gap-5 mr-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateToProject(track.project_id, track.response_id);
                }}
                onMouseEnter={() => setActiveTooltip({ trackId: track.response_id, type: 'goTo' })}
                onMouseLeave={() => setActiveTooltip({ trackId: null, type: null })}
                className="text-[#878188] hover:text-white relative"
              >
                <ArrowUpRightFromSquare className="w-4 h-4" />
                {activeTooltip.trackId === track.response_id && activeTooltip.type === 'goTo' && (
                  <div className="absolute text-white text-xs p-1 pl-1.5 rounded top-6 bg-white/10 backdrop-blur-md left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                    Go to project
                  </div>
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleGetStems(e, track);
                }}
                onMouseEnter={() => setActiveTooltip({ trackId: track.response_id, type: 'stems' })}
                onMouseLeave={() => setActiveTooltip({ trackId: null, type: null })}
                className="text-[#878188] hover:text-white relative"
              >
                <img src="/updates-modal/feature-update-icon-2.svg" alt="Get stems" className="w-4 h-4 opacity-50 hover:opacity-100" />
                {activeTooltip.trackId === track.response_id && activeTooltip.type === 'stems' && (
                  <div className="absolute text-white text-xs p-1 pl-1.5 rounded top-6 bg-white/10 backdrop-blur-md left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                  Get stems
                  </div>
                )}
              </button>

              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDownload(track);
                }}
                onMouseEnter={() => setActiveTooltip({ trackId: track.response_id, type: 'download' })}
                onMouseLeave={() => setActiveTooltip({ trackId: null, type: null })}
                className="text-[#878188] hover:text-white relative"
              >
                <ArrowShapeDownToLine className="w-4 h-4" />
                {activeTooltip.trackId === track.response_id && activeTooltip.type === 'download' && (
                  <div className="absolute text-white text-xs p-1 pl-1.5 rounded top-6 bg-white/10 backdrop-blur-md left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                    Download
                  </div>
                )}
              </button>
              <button
                onClick={(e) => handleUnlike(e, track)}
                onMouseEnter={() => setActiveTooltip({ trackId: track.response_id, type: 'unlike' })}
                onMouseLeave={() => setActiveTooltip({ trackId: null, type: null })}
                className="text-[#878188] hover:text-white relative"
              >
                <ThumbsUpFill className="w-4 h-4" />
                {activeTooltip.trackId === track.response_id && activeTooltip.type === 'unlike' && (
                  <div className="absolute text-white text-xs p-1 pl-1.5 rounded top-6 bg-white/10 backdrop-blur-md left-1/2 -translate-x-1/2 whitespace-nowrap z-10">
                    Unlike
                  </div>
                )}
              </button>
            </div>
          </div>
        </div>
      ))}
      {isGeneratingStems ? (
        <ProgressInfoToast
          type="info"
          message={`${selectedTrack?.name || 'Track'} - Stems are being generated...`}
          loading={true}
          excludeCloseButton={false}
          className="-translate-x-1/2 w-[90%] md:w-fit md:min-w-[360px]"
          imageSrc={selectedTrack?.imageSrc}
          currentStep={stemProgress}
          totalSteps={3}
          onClose={() => setIsGeneratingStems(false)}
        />
      ) : toastMessage && (
        <Toast
          type={toastType}
          message={toastMessage}
          onClose={() => setToastMessage(null)}
        />
      )}
      {isDownloadModalOpen && selectedTrack && (
        <DownloadModal
          title={selectedTrack.name}
          description={selectedTrack.description}
          duration={typeof selectedTrack.duration === "string" ? selectedTrack.duration : formatTime(selectedTrack.duration) || "0:00"}
          imageSrc={selectedTrack.imageSrc}
          ai_response_id={selectedTrack.response_id}
          onClose={() => setIsDownloadModalOpen(false)}
          onError={(message) => {
            setToastMessage(message);
            setToastType("error");
            setIsDownloadModalOpen(false);
          }}
        />
      )}
    </div>
  );
};

export default ProjectLikedTracksList;